import { ColumnDef } from "@tanstack/react-table";
import { Avatar } from "@mui/material";
import { enumToStringConverter } from "../../../../../helpers/appHelpers";

// Type definitions
interface OrganizationRole {
	name: string;
}

interface AccountUserRow {
	id: number;
	firstName: string;
	lastName: string;
	name?: string; // Sometimes used instead of firstName/lastName
	imageUrl?: string;
	emailAddress: string;
	status: "ACTIVE" | "INACTIVE";
	organizationRole: OrganizationRole;
	createdAt?: string;
	modifiedAt?: string;
}

// Helper function for status colors
const getUserStatusColor = (status: string) => {
	switch (status) {
		case "ACTIVE":
			return "#4CAF50";
		case "INACTIVE":
			return "#F44336";
		default:
			return "#808080";
	}
};

// ========== Account Users Columns ==========
export const accountUsersColumns: ColumnDef<AccountUserRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 250,
		cell: ({ row }) => {
			const firstName = row.original.firstName || "";
			const lastName = row.original.lastName || "";
			const imageUrl = row.original.imageUrl;

			return (
				<div className="flex items-center space-x-4">
					<Avatar
						src={imageUrl || ""}
						alt="Dp"
						className="w-10 h-10 rounded-full"
					/>
					<p className="truncate">{firstName + " " + lastName}</p>
				</div>
			);
		},
	},
	{
		id: "emailAddress",
		accessorKey: "emailAddress",
		header: "Email Address",
		size: 250,
	},
	{
		id: "status",
		accessorKey: "status",
		header: "Status",
		size: 180,
		cell: ({ row }) => {
			const status = row.original.status;
			const color = getUserStatusColor(status);

			return (
				<div
					className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
					style={{ color }}
				>
					<div
						className="absolute inset-0"
						style={{
							background: color,
							opacity: "15%",
						}}
					/>
					<p>{enumToStringConverter(status)}</p>
				</div>
			);
		},
	},
	{
		id: "organizationRole",
		accessorKey: "organizationRole",
		header: "Organization Role",
		size: 250,
		cell: ({ row }) => row.original.organizationRole?.name || "",
	},
];

export const accountUsersColumnVisibility: Record<string, boolean> = {
	name: true,
	emailAddress: true,
	status: true,
	organizationRole: true,
};
